import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, RadioTower, Loader2, BarChart3, MousePointerClick, Eye, Filter, ShieldCheck, Play } from "lucide-react";

/**
 * Admin surface: homepage-variant A/B toggle + Web Push broadcast composer.
 * Mounted at /admin/cms/homepage-and-push (linked from CMS Pages + Alerts).
 */
export default function CmsHomepageAndPush() {
  const [variant, setVariant] = useState("new");
  const [busy, setBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [subs, setSubs] = useState({ total: 0, configured: false });

  const [title, setTitle] = useState("New deals near you");
  const [body, setBody] = useState("");
  const [city, setCity] = useState("");
  const [url, setUrl] = useState("/food-pulse/deals");
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState({ items: [], total: 0, totals: null, cities: [], loading: true });
  const [filters, setFilters] = useState({ city: "", dry_run: "any", since: "", until: "" });
  const [prune, setPrune] = useState({ runs: [], subs_total: 0, latest: null, loading: true, busy: false });

  const loadHistory = useCallback(async () => {
    setHistory((h) => ({ ...h, loading: true }));
    try {
      const params = { limit: 20 };
      if (filters.city.trim()) params.city = filters.city.trim();
      if (filters.dry_run === "yes") params.dry_run = true;
      if (filters.dry_run === "no") params.dry_run = false;
      if (filters.since) params.since = new Date(filters.since).toISOString();
      if (filters.until) params.until = new Date(filters.until).toISOString();
      const r = await http.get("/admin/push/broadcasts", { params });
      setHistory({
        items: r.data?.items || [],
        total: r.data?.total || 0,
        totals: r.data?.totals || null,
        cities: r.data?.cities || [],
        loading: false,
      });
    } catch {
      setHistory((h) => ({ ...h, items: [], total: 0, totals: null, loading: false }));
    }
  }, [filters]);

  const loadPrune = useCallback(async () => {
    setPrune((p) => ({ ...p, loading: true }));
    try {
      const r = await http.get("/admin/push/prune/history", { params: { limit: 8 } });
      setPrune({
        runs: r.data?.runs || [],
        subs_total: r.data?.subs_total || 0,
        latest: r.data?.latest || null,
        loading: false,
        busy: false,
      });
    } catch {
      setPrune({ runs: [], subs_total: 0, latest: null, loading: false, busy: false });
    }
  }, []);

  const runPruneNow = async () => {
    setPrune((p) => ({ ...p, busy: true }));
    try {
      await http.post("/admin/push/prune", {});
      toast.success("Prune run complete");
      loadPrune();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prune failed");
      setPrune((p) => ({ ...p, busy: false }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [v, k] = await Promise.all([
          http.get("/cms/homepage-variant"),
          http.get("/push/public-key"),
        ]);
        setVariant(v.data?.variant || "new");
        setSubs({ total: 0, configured: !!k.data?.configured });
      } catch { /* ignore */ }
    })();
    loadPrune();
  }, [loadPrune]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const saveVariant = async (v) => {
    setBusy(true);
    try {
      await http.put("/admin/cms/homepage-variant", { variant: v });
      setVariant(v);
      toast.success(`Homepage variant → ${v}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const broadcast = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setPushBusy(true);
    try {
      const r = await http.post("/admin/push/broadcast", {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || "/food-pulse/deals",
        city: city.trim() || undefined,
        dry_run: dryRun,
      });
      setResult(r.data);
      toast.success(dryRun ? `Dry-run → ${r.data.targeted} targets` : `Delivered ${r.data.delivered} / ${r.data.targeted}`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="cms-homepage-push">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Homepage &amp; Push</h1>
      <p className="mt-1 text-sm text-slate-500">A/B the FoodPulse home + broadcast locality-aware push notifications.</p>

      {/* Homepage variant switcher */}
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="cms-variant-section">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Homepage variant</div>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {[
            { v: "new",      label: "New design",  hint: "The redesigned v3 home." },
            { v: "legacy",   label: "Legacy",      hint: "The prior editorial home." },
            { v: "ab_split", label: "50/50 A/B",   hint: "Deterministic per-visitor split." },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => saveVariant(opt.v)}
              disabled={busy}
              data-testid={`cms-variant-${opt.v}`}
              className={`rounded-lg border p-3 text-left transition ${
                variant === opt.v
                  ? "border-emerald-400 bg-emerald-50"
                  : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50"
              }`}
            >
              <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
              <div className="mt-0.5 text-xs text-slate-500">{opt.hint}</div>
              {variant === opt.v && <div className="mt-2 text-[11px] font-medium text-emerald-700">✓ Active</div>}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
          <Link to="/food-pulse" target="_blank" className="text-emerald-700 hover:underline">Preview new →</Link>
          <span>·</span>
          <Link to="/food-pulse/legacy" target="_blank" className="text-emerald-700 hover:underline">Preview legacy →</Link>
        </div>
      </section>

      {/* Push broadcast */}
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="cms-push-section">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Web push broadcast</div>
            <p className="mt-0.5 text-xs text-slate-500">Only reaches devices that installed the FoodPulse PWA and opted-in.</p>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${subs.configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
            {subs.configured ? "VAPID configured" : "VAPID missing — dry-run only"}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs text-slate-600">Title *</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cms-push-title" />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs text-slate-600">Body</label>
            <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} data-testid="cms-push-body" placeholder="3 new deals live in your area today — from local farms and markets." />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">Filter by city (optional)</label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Portland" data-testid="cms-push-city" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">Deep-link URL</label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div className="md:col-span-2 flex items-center gap-2 text-xs text-slate-600">
            <input id="dry-run" type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="cms-push-dryrun" />
            <label htmlFor="dry-run">Dry run — just report reachable subscribers without sending</label>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={broadcast} disabled={pushBusy || !title.trim()} data-testid="cms-push-send" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {pushBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            {pushBusy ? "Sending…" : (dryRun ? "Run dry-run" : "Send broadcast")}
          </Button>
        </div>

        {result && (
          <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-700" data-testid="cms-push-result">
            <div className="flex items-center gap-2">
              <RadioTower className="h-3.5 w-3.5 text-slate-400" />
              <span>Targeted <b>{result.targeted}</b> · Delivered <b>{result.delivered}</b> · Failed <b>{result.failed}</b> {result.dry_run && <em className="text-amber-700">(dry-run)</em>}</span>
            </div>
            {result.errors?.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-rose-600">
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* Push Performance — delivery / click tracker per broadcast */}
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="cms-push-analytics">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <BarChart3 className="h-3.5 w-3.5" /> Push performance
            </div>
            <p className="mt-0.5 text-xs text-slate-500">Which "3 new deals in {`{city}`}" pings actually convert.</p>
          </div>
          {history.totals && (
            <div className="hidden gap-4 text-right text-xs text-slate-600 sm:flex" data-testid="cms-push-totals">
              <div><div className="text-slate-400">Delivered</div><b className="text-slate-900">{history.totals.delivered}</b></div>
              <div><div className="text-slate-400"><span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Shown</span></div><b className="text-slate-900">{history.totals.impressions}</b></div>
              <div><div className="text-slate-400"><span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> Clicks</span></div><b className="text-slate-900">{history.totals.clicks}</b></div>
              <div><div className="text-slate-400">CTR</div><b className="text-emerald-700">{history.totals.ctr}%</b></div>
            </div>
          )}
        </div>

        {/* Filter rail — city / dry-run / date range */}
        <div className="mt-4 grid grid-cols-1 gap-2 rounded-lg border border-slate-100 bg-slate-50/60 p-3 md:grid-cols-5" data-testid="cms-push-filters">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 md:col-span-5">
            <Filter className="h-3 w-3" /> Filter history
          </div>
          <div>
            <label className="block text-[10px] text-slate-500">City</label>
            <select
              value={filters.city}
              onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
              data-testid="cms-push-filter-city"
              className="mt-0.5 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
            >
              <option value="">All cities</option>
              {history.cities.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-slate-500">Send type</label>
            <select
              value={filters.dry_run}
              onChange={(e) => setFilters((f) => ({ ...f, dry_run: e.target.value }))}
              data-testid="cms-push-filter-dryrun"
              className="mt-0.5 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
            >
              <option value="any">All</option>
              <option value="no">Real sends only</option>
              <option value="yes">Dry-runs only</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-slate-500">Since</label>
            <input
              type="date"
              value={filters.since}
              onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))}
              data-testid="cms-push-filter-since"
              className="mt-0.5 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-500">Until</label>
            <input
              type="date"
              value={filters.until}
              onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))}
              data-testid="cms-push-filter-until"
              className="mt-0.5 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ city: "", dry_run: "any", since: "", until: "" })}
              data-testid="cms-push-filter-reset"
              className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
            >
              Reset
            </button>
          </div>
        </div>

        {history.loading ? (
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading history…
          </div>
        ) : history.items.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500" data-testid="cms-push-empty">
            No broadcasts match these filters — widen the criteria or reset.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs" data-testid="cms-push-history">
              <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="pb-2 pr-3">Title</th>
                  <th className="pb-2 pr-3">City</th>
                  <th className="pb-2 pr-3 text-right">Targeted</th>
                  <th className="pb-2 pr-3 text-right">Delivered</th>
                  <th className="pb-2 pr-3 text-right">Shown</th>
                  <th className="pb-2 pr-3 text-right">Clicks</th>
                  <th className="pb-2 text-right">CTR</th>
                </tr>
              </thead>
              <tbody>
                {history.items.map((b) => (
                  <tr key={b.bid} className="border-t border-slate-100" data-testid={`cms-push-row-${b.bid}`}>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-slate-900">{b.title}</div>
                      <div className="text-[10px] text-slate-400">{b.created_at?.slice(0, 16).replace("T", " ")}{b.dry_run ? " · dry-run" : ""}</div>
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{b.city || "—"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{b.targeted}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{b.delivered}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{b.impressions || 0}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{b.clicks || 0}</td>
                    <td className="py-2 text-right tabular-nums font-semibold text-emerald-700">{b.ctr}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Subscription list health — surface the weekly prune loop's output */}
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="cms-push-prune">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5" /> Subscription list health
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              Weekly prune silently pings every subscription and drops expired endpoints so broadcast metrics stay honest.
            </p>
          </div>
          <button
            onClick={runPruneNow}
            disabled={prune.busy}
            data-testid="cms-push-prune-run"
            className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            {prune.busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            {prune.busy ? "Running…" : "Run prune now"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="cms-push-prune-summary">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Total subscribers</div>
            <div className="mt-0.5 text-xl font-semibold text-slate-900">{prune.subs_total}</div>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Last scanned</div>
            <div className="mt-0.5 text-xl font-semibold text-slate-900">{prune.latest?.scanned ?? "—"}</div>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Expired dropped</div>
            <div className="mt-0.5 text-xl font-semibold text-rose-600">{prune.latest?.expired ?? "—"}</div>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Kept alive</div>
            <div className="mt-0.5 text-xl font-semibold text-emerald-700">{prune.latest?.kept ?? "—"}</div>
          </div>
        </div>

        {prune.loading ? (
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading prune history…
          </div>
        ) : prune.runs.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500" data-testid="cms-push-prune-empty">
            No prune runs yet — click "Run prune now" to seed the history.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs" data-testid="cms-push-prune-runs">
              <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="pb-2 pr-3">Run</th>
                  <th className="pb-2 pr-3">Trigger</th>
                  <th className="pb-2 pr-3 text-right">Scanned</th>
                  <th className="pb-2 pr-3 text-right">Expired</th>
                  <th className="pb-2 pr-3 text-right">Kept</th>
                  <th className="pb-2 pr-3 text-right">Errored</th>
                  <th className="pb-2">Mode</th>
                </tr>
              </thead>
              <tbody>
                {prune.runs.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`cms-push-prune-run-${r.id}`}>
                    <td className="py-2 pr-3 text-slate-600">{(r.run_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 pr-3 text-slate-600">{r.trigger}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{r.scanned}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-rose-600">{r.expired}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">{r.kept}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-600">{r.errored}</td>
                    <td className="py-2 text-slate-500">{r.dry_run ? <em>dry-run</em> : "live"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
